import { useShallow } from 'zustand/react/shallow';
import { startTransition, useCallback, useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';
import { type AutogenConfig } from '@pkgs/components/types/types';
import { type SearchModuleTranslationKey } from '@pkgs/components/types/typedTranslationKeys';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { getSelectedPropertyTypes, getSelectedSubTypes } from '@pkgs/components/hooks/stores/search/slices/objectTypeFilterSlice';
import { useShopsByIds } from '@pkgs/components/hooks/queries/useShopsByIds';
import { useListings } from '@pkgs/components/hooks/queries/useListings';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { useAgentProfile } from '@pkgs/components/hooks/queries/useAgentProfile';
import { BusinessArea, PropertyMarketingType, PropertyType } from '@ev-tech/search-modules-api';

import { TotalResultsCount } from './TotalResultsCount';
import { SeoLinks } from './SeoLinks';
import { AiExtractedFilterChipsRow } from './AiExtractedFilterChipsRow';
import { Switch } from '../Switch/Switch';
import { SortBySelect } from '../SortBySelect/SortBySelect';
import { Skeleton } from '../Skeleton/Skeleton';
import { LipaChip } from '../LipaChip/LipaChip';
import { Headline } from '../Headline/Headline';
import { AgentFilterChip } from '../AgentFilterChip/AgentFilterChip';

import styles from './InfoRow.module.css';

const infoRowVariants = cva(styles.baseClass, {
  variants: {
    variant: {
      default: null,
      autogen: styles.autogenVariant,
    },
  },
});

type SeoHeadlineTranslationKey = Extract<SearchModuleTranslationKey, `search.seo.headline.${string}`>;

type SeoHeadlineObjectKey =
  Extract<
    SeoHeadlineTranslationKey,
    `search.seo.headline.${'rent' | 'sale'}.${string}`
  > extends `search.seo.headline.${'rent' | 'sale'}.${infer TObjectKey}`
    ? TObjectKey
    : never;

type SeoHeadlineBaseTranslationKey = Extract<SeoHeadlineTranslationKey, `search.seo.headline.${'rent' | 'sale'}.${SeoHeadlineObjectKey}`>;

const getSeoHeadlineTranslationKey = (
  propertyMarketingType: PropertyMarketingType,
  objectType: string,
  isLuxury: boolean,
): SeoHeadlineTranslationKey | undefined => {
  if (
    isLuxury &&
    propertyMarketingType === PropertyMarketingType.sale &&
    (objectType === PropertyType.house || objectType === PropertyType.apartment || objectType === BusinessArea.residential)
  ) {
    return `search.seo.headline.sale.${objectType}.luxury`;
  } else {
    return `search.seo.headline.${propertyMarketingType}.${objectType}` as SeoHeadlineBaseTranslationKey;
  }
};

export const InfoRow = ({
  autogenConfig,
  isMapView,
  updateIsMapView,
}: {
  autogenConfig?: AutogenConfig;
  isMapView: boolean;
  updateIsMapView: (isMapView: boolean) => void;
}) => {
  const { featureFlags, t } = useAppContext();
  const { hasMounted, isWebGLEnabled } = useMapVisibilityStore(
    useShallow((state) => ({
      hasMounted: state.hasMounted,
      isWebGLEnabled: state.isWebGLEnabled,
    })),
  );
  const { isFetching: isListingsFetching, data: results } = useListings();
  const { isPending: isAiSearchFetching } = useAiSearch();
  const { breakpoint } = useBreakpoint();
  const isLipaPage = useIsLipaPage();
  const placeName = useSearchStore((state) => state.placeName);
  const propertyTypeState = useSearchStore((state) => state.propertyTypeState);
  const objectTypeCategory = useSearchStore((state) => state.objectTypeCategory);
  const propertyMarketingType = useSearchStore((state) => state.marketingType);
  const promotedBy = useSearchStore((state) => state.promotedBy);
  const agentId = useSearchStore((state) => state.agentId);
  const { data: agentProfile } = useAgentProfile();
  const isFetching = isListingsFetching || isAiSearchFetching;
  const [selectedMapView, setSelectedMapView] = useState(isMapView);

  useEffect(() => {
    setSelectedMapView(isMapView);
  }, [isMapView]);

  const handleMapViewChange = useCallback(
    (nextMapView: boolean) => {
      setSelectedMapView(nextMapView);
      startTransition(() => updateIsMapView(nextMapView));
    },
    [updateIsMapView],
  );

  const { shopNames } = useShopsByIds();

  const selectedSeoObjectType =
    getSelectedSubTypes(propertyTypeState)[0] || getSelectedPropertyTypes(propertyTypeState, objectTypeCategory)[0] || objectTypeCategory;

  const autogenHeadlineTranslationKey = getSeoHeadlineTranslationKey(
    propertyMarketingType,
    selectedSeoObjectType,
    Boolean(autogenConfig?.isLuxury),
  );

  const autogenHeadlineText = autogenHeadlineTranslationKey
    ? t(autogenHeadlineTranslationKey, {
        location: placeName ?? t('search.seo.headline.searchArea'),
        totalResults: results.listingsTotal,
        defaultValue: '',
      })
    : '';

  const showsTotalResultsCount =
    !autogenConfig &&
    (results.listingsTotal > 0 || (results.listingsTotal === 0 && Boolean(breakpoint && breakpoint >= Breakpoints.TABLET_LANDSCAPE)));

  return (
    <div
      className={infoRowVariants({
        variant: autogenConfig ? 'autogen' : 'default',
      })}
      data-test-id="search-components_info-row"
    >
      <div className={styles.chipsContainer}>
        {autogenConfig && (
          <div className={styles.autogenInfoRowContainer}>
            <Skeleton loading={isFetching}>
              <Headline className={styles.headline} label={autogenHeadlineText} as="h1" size="h5" />
            </Skeleton>
            {autogenConfig.seoPagesLinks.length > 0 && <SeoLinks links={autogenConfig.seoPagesLinks} />}
          </div>
        )}
        {!!shopNames.length && isLipaPage && <LipaChip className={styles.lipaChip} />}
        {!!(agentProfile && (promotedBy || agentId)) && <AgentFilterChip agentProfile={agentProfile} />}
        <AiExtractedFilterChipsRow isMapView={isMapView} />
      </div>
      <div className={styles.infoContainer}>
        <div className={styles.resultsGroup}>
          {showsTotalResultsCount && <TotalResultsCount />}
          {(results.listingsTotal > 0 || isFetching) && <SortBySelect />}
        </div>
        {featureFlags.isMapEnabled && hasMounted && isWebGLEnabled && (
          <Switch
            className={styles.mapSwitch}
            label={t('search.searchbar.mapViewToggle.label')}
            isSelected={selectedMapView}
            onChange={handleMapViewChange}
            data-testid="search-components_info-row_map-toggle"
          />
        )}
      </div>
    </div>
  );
};
